import { VOD, VOD_MOVIE, VOD_SERIES_EPISODE, WATCH } from '../types';
import { actions, constants, selectors } from 'app-webtech-core';

import find from 'lodash/find';
import get from 'lodash/get';
import { redirect } from 'app-source/actions/redirect';

const { VOD_CONTENT_TYPE } = constants.vodContent;

function waitState(
  getState,
  selector,
  compareFunction = (value) => value !== undefined,
  checkRate = 100,
  timeout = 10000000,
) {
  return new Promise((resolve, reject) => {
    let timeoutRef;
    const interval = setInterval(() => {
      const value = selector(getState());
      if (compareFunction(value)) {
        clearInterval(interval);
        if (timeoutRef) {
          clearTimeout(timeoutRef);
        }
        resolve(value);
      }
    }, checkRate);
    if (timeout > 0) {
      timeoutRef = setTimeout(() => {
        clearInterval(interval);
        reject(new Error('Timeout'));
      }, timeout);
    }
  });
}

const vodEnabledCheckThunk = async (dispatch, getState) => {
  // ignore server check
  if (get(getState(), 'application.setup.isServer')) {
    return true;
  }

  await waitState(
    getState,
    (state) => get(state, 'application.isConfigured'),
    (value) => value === true,
  );
  const enableVOD = selectors.application.enableVOD(getState());

  if (!enableVOD) {
    return dispatch(redirect({ type: WATCH }));
  }
};

function vodThunk(itemIdResolver, notValidItem, notPlaying, samePlaying, differentPlaying) {
  return async (dispatch, getState) => {
    try {
      const response = await vodEnabledCheckThunk(dispatch, getState);
      if (response) {
        return response;
      }
      const vodContent = await waitState(getState, (state) =>
        get(state, 'vodContent.entities.vodContent'),
      );

      const itemId = await itemIdResolver(getState, vodContent, dispatch);
      const state = getState();
      const currentPlayingId = get(state, 'player.content.id');
      if (itemId) {
        if (!currentPlayingId) {
          if (notPlaying) {
            notPlaying(dispatch, itemId, state);
          }
        } else if (currentPlayingId === itemId) {
          if (samePlaying) {
            samePlaying(dispatch, itemId, state);
          }
        } else if (currentPlayingId !== itemId) {
          if (differentPlaying) {
            differentPlaying(dispatch, itemId, state);
          }
        }
      } else if (notValidItem) {
        notValidItem(dispatch, itemId, state);
      }
    } catch (err) {
      console.error(err);
    }
  };
}

export const vodRootThunk = vodEnabledCheckThunk;

export const vodMovieThunk = vodThunk(
  async (getState, vodContent, dispatch) => {
    const movieSlug = get(getState(), 'location.payload.movieSlug');
    let item = find(
      vodContent,
      (content) => content.type === VOD_CONTENT_TYPE.MOVIES && content.slug === movieSlug,
    );
    let foundItemID = get(item, '_id');
    if (!foundItemID) {
      await dispatch(actions.vodContent.fetchBySlug(movieSlug));
      const newVodContent = await waitState(getState, (state) =>
        get(state, 'vodContent.entities.vodContent'),
      );
      item = find(
        newVodContent,
        (content) => content.type === VOD_CONTENT_TYPE.MOVIES && content.slug === movieSlug,
      );
      foundItemID = get(item, '_id');
    }

    return foundItemID;
  },
  (dispatch) => {
    dispatch(redirect({ type: VOD }));
  },
  (dispatch, itemId) => {
    dispatch(actions.vodGuide.playItem(itemId));
    dispatch(actions.player.enteredFullscreen());
  },
  (dispatch) => {
    dispatch(actions.vodGuide.hideItemDetail());
  },
  (dispatch, itemId) => {
    dispatch(actions.vodGuide.playItem(itemId));
    dispatch(actions.player.enteredFullscreen());
  },
);

export const vodSeriesThunk = vodThunk(async (getState, _, dispatch) => {
  const seriesSlug = get(getState(), 'location.payload.seriesSlug');
  await dispatch(actions.vodContent.fetchBySlug(seriesSlug));

  const vodContent = await waitState(getState, (state) =>
    get(state, 'vodContent.entities.vodContent'),
  );

  const item = find(
    vodContent,
    (content) => content.type === VOD_CONTENT_TYPE.SERIES && content.slug === seriesSlug,
  );

  const itemId = get(item, '_id');
  await dispatch(actions.vodGuide.showItemDetail({ id: itemId, isSeries: true }));

  // return null to bail out of the vodThunk asap
  return null;
});

export const vodSeriesEpisodeThunk = vodThunk(
  async (getState, _, dispatch) => {
    const seriesSlug = get(getState(), 'location.payload.seriesSlug');
    await dispatch(actions.vodContent.fetchBySlug(seriesSlug));

    const vodContent = await waitState(getState, (state) =>
      get(state, 'vodContent.entities.vodContent'),
    );

    const episodeSlug = get(getState(), 'location.payload.episodeSlug');
    const item = find(
      vodContent,
      (content) => content.type === VOD_CONTENT_TYPE.EPISODE && content.slug === episodeSlug,
    );
    return get(item, '_id');
  },
  (dispatch) => {
    dispatch(redirect({ type: VOD }));
  },
  (dispatch, itemId) => {
    dispatch(actions.vodGuide.playItem(itemId));
    dispatch(actions.player.enteredFullscreen());
  },
  (dispatch) => {
    dispatch(actions.vodGuide.hideItemDetail());
  },
  (dispatch, itemId) => {
    dispatch(actions.vodGuide.playItem(itemId));
    dispatch(actions.player.enteredFullscreen());
  },
);

export function oldVodUrlsThunk(dispatch, getState) {
  const state = getState();
  const slug = get(state, 'location.payload.slug');

  let type = VOD;
  let payload;
  if (slug) {
    if (slug.indexOf('/') > 0) {
      type = VOD_SERIES_EPISODE;
      const [seriesSlug, episodeSlug] = slug.split('/');
      payload = {
        seriesSlug,
        episodeSlug,
      };
    } else {
      type = VOD_MOVIE;
      payload = {
        movieSlug: slug,
      };
    }
  }

  dispatch(redirect({ type, payload }));
}
